/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { AppGit, AppGit$inboundSchema } from "./appgit.js";
import { AppOCI, AppOCI$inboundSchema } from "./appoci.js";

/**
 * The configured source used to create deployments. Omitted for historical
 *
 * @remarks
 * apps whose source could not be classified.
 */
export const SourceType = {
  Git: "git",
  Oci: "oci",
} as const;
/**
 * The configured source used to create deployments. Omitted for historical
 *
 * @remarks
 * apps whose source could not be classified.
 */
export type SourceType = ClosedEnum<typeof SourceType>;

export type App = {
  /**
   * The unique identifier of the app, generated by Teral.
   *
   * @remarks
   */
  id: string;
  /**
   * Human-readable name for this app.
   *
   * @remarks
   */
  name: string;
  /**
   * URL-safe handle for this app, unique within its project.
   *
   * @remarks
   * Chosen at creation time.
   */
  slug: string;
  /**
   * The configured source used to create deployments. Omitted for historical
   *
   * @remarks
   * apps whose source could not be classified.
   */
  sourceType?: SourceType | undefined;
  /**
   * The connected GitHub repository and the branch its deployments track.
   *
   * @remarks
   * Omitted when the app has no repository connected (for example a
   * prebuilt OCI image app).
   */
  git?: AppGit | undefined;
  /**
   * The configured OCI image source. Omitted when the app is not OCI-sourced.
   *
   * @remarks
   */
  oci?: AppOCI | undefined;
  /**
   * The identifier of the deployment currently serving this app.
   *
   * @remarks
   * Omitted if the app has no active deployment yet.
   */
  currentDeploymentId?: string | undefined;
  /**
   * Whether the app is currently serving a rolled-back deployment rather than its latest one.
   *
   * @remarks
   */
  isRolledBack: boolean;
  /**
   * Whether delete protection is enabled for this app.
   *
   * @remarks
   * When true, the app cannot be deleted until protection is disabled.
   */
  deleteProtection: boolean;
  /**
   * Unix timestamp in milliseconds when the app was created.
   *
   * @remarks
   */
  createdAt: number;
  /**
   * Unix timestamp in milliseconds when the app was last updated.
   *
   * @remarks
   * Omitted if the app has never been updated.
   */
  updatedAt?: number | undefined;
};

/** @internal */
export const SourceType$inboundSchema: z.ZodNativeEnum<typeof SourceType> = z
  .nativeEnum(SourceType);

/** @internal */
export const App$inboundSchema: z.ZodType<App, z.ZodTypeDef, unknown> = z
  .object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    sourceType: SourceType$inboundSchema.optional(),
    git: AppGit$inboundSchema.optional(),
    oci: AppOCI$inboundSchema.optional(),
    currentDeploymentId: z.string().optional(),
    isRolledBack: z.boolean(),
    deleteProtection: z.boolean(),
    createdAt: z.number().int(),
    updatedAt: z.number().int().optional(),
  });

export function appFromJSON(
  jsonString: string,
): SafeParseResult<App, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => App$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'App' from JSON`,
  );
}
